<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <app-page-container v-else>
      <h1 class="title">
        {{ car.plate_number }} <span v-if="car.rental_company.name">({{ car.rental_company.name }})</span>
      </h1>
      <app-cells>
        <app-textarea
          v-model="car.notes"
          rows="5"
          placeholder="Write a notes"
        />
      </app-cells>
      <app-flex-grid
        :rows="[
          [1, 1, 1, 1],
          ['210px', '210px', '210px'],
          ['115px', '115px', '115px', '115px', '115px'],
          ['115px', '115px', '115px', '115px'],
          [1],
          ['210px', '210px'],
          [1, 1, 1, 1]
        ]"
      >
        <template #header-1>
          <app-cells :indent="false" gap="10px" margin-bottom="15px">
            <img src="@/assets/img/personal-details.svg" alt="personal">
            <span>Car details</span>
            <app-button @click.native="onSaveCarDetails" size="small" ref="save_details">Save</app-button>
          </app-cells>
        </template>
        <template #col-1-1>
          <app-form-field label="Company" required>
            <app-select
              v-model="car.rental_company"
              :options="company_options"
              :clearable="false"
              placeholder="Company"
              :error="$v.car.rental_company.$error"
            />
            <template #error>
              <div v-if="$v.car.rental_company.$dirty && !$v.car.rental_company.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-1-2>
          <app-form-field label="Plate number" label-for="plate_number" required>
            <app-input
              v-model="car.plate_number"
              placeholder="Plate number"
              id="plate_number"
              name="plate_number"
              :error="$v.car.plate_number.$error"
            />
            <template #error>
              <div v-if="$v.car.plate_number.$dirty && !$v.car.plate_number.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-1-3>
          <app-form-field label="Located" required>
            <app-input
              v-model="car.located"
              placeholder="Located"
              id="located"
              name="located"
              :error="$v.car.located.$error"
            />
            <template #error>
              <div v-if="$v.car.located.$dirty && !$v.car.located.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-1-4>
          <app-form-field label="Active">
            <app-switcher v-model="car.active" />
          </app-form-field>
        </template>
        <template #col-2-1>
          <app-form-field label="Seats" label-for="rental_car_seats" required>
            <app-input
              v-model.number="car.seats"
              type="number"
              placeholder="Seats"
              id="rental_car_seats"
              name="rental_car_seats"
              :error="$v.car.seats.$error"
            />
            <template #error>
              <div v-if="$v.car.seats.$dirty && !$v.car.seats.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-2-2>
          <app-form-field label="Year" label-for="rental_car_year" required>
            <app-input
              v-model.number="car.year"
              type="number"
              placeholder="Year"
              id="rental_car_year"
              name="rental_car_year"
              :error="$v.car.year.$error"
            />
            <template #error>
              <div v-if="$v.car.year.$dirty && !$v.car.year.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-2-3>
          <app-form-field label="Car type" required>
            <app-select
              v-model="car.car_type"
              :options="car_type_options"
              :clearable="false"
              placeholder="Car type"
              :error="$v.car.car_type.$error"
            />
            <template #error>
              <div v-if="$v.car.car_type.$dirty && !$v.car.car_type.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-3-1>
          <app-form-field label="Seats movable">
            <app-switcher v-model="car.seats_movable" />
          </app-form-field>
        </template>
        <template #col-3-2>
          <app-form-field label="Medical kit">
            <app-switcher v-model="car.medical_kit" />
          </app-form-field>
        </template>
        <template #col-3-3>
          <app-form-field label="Front a/c">
            <app-switcher v-model="car.front_a_c" />
          </app-form-field>
        </template>
        <template #col-3-4>
          <app-form-field label="Rear a/c">
            <app-switcher v-model="car.rear_a_c" />
          </app-form-field>
        </template>
        <template #col-3-5>
          <app-form-field label="Wifi">
            <app-switcher v-model="car.wifi" />
          </app-form-field>
        </template>
        <template #col-4-1>
          <app-form-field label="Books">
            <app-switcher v-model="car.books" />
          </app-form-field>
        </template>
        <template #col-4-2>
          <app-form-field label="Fridge">
            <app-switcher v-model="car.fridge" />
          </app-form-field>
        </template>
        <template #col-4-3>
          <app-form-field label="Hot Pots">
            <app-switcher v-model="car.hot_pots" />
          </app-form-field>
        </template>
        <template #col-4-4>
          <app-form-field label="Coffee bucket">
            <app-switcher v-model="car.coffee_bucket" />
          </app-form-field>
        </template>
        <template #col-5-1>
          <app-form-field label="Registration Card">
            <app-uploader
              v-model="car.registration_card"
              :extensions="[
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/zip',
                'application/pdf',
                'image/jpeg',
                'image/png',
                'image/tiff',
                'image/bmp'
                ]"
              class="custom-uploader--flex"
              formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
            />
          </app-form-field>
        </template>
        <template #col-6-1>
          <app-form-field v-if="car.wifi" label="SSID Name" label-for="ssid_name">
            <app-input
              v-model="car.ssid"
              placeholder="SSID Name"
              id="ssid_name"
              name="ssid_name"
            />
          </app-form-field>
        </template>
        <template #col-6-2>
          <app-form-field v-if="car.wifi" label="SSID Password" label-for="ssid_password">
            <app-input
              v-model="car.password"
              placeholder="SSID Password"
              id="ssid_password"
              name="ssid_password"
            />
          </app-form-field>
        </template>
        <template #col-7-1>
          <app-form-field label="Binocular" label-for="binocular" required>
            <app-input
              v-model.number="car.binocular"
              type="number"
              placeholder="123"
              id="binocular"
              name="binocular"
              :error="$v.car.binocular.$error"
            />
            <template #error>
              <div v-if="$v.car.binocular.$dirty && !$v.car.binocular.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-7-2>
          <app-form-field label="Table" label-for="table_other" required>
            <app-input
              v-model.number="car.table"
              type="number"
              placeholder="123"
              id="table_other"
              name="table_other"
              :error="$v.car.table.$error"
            />
            <template #error>
              <div v-if="$v.car.table.$dirty && !$v.car.table.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-7-3>
          <app-form-field label="Chairs" label-for="chairs" required>
            <app-input
              v-model.number="car.chairs"
              type="number"
              placeholder="123"
              id="chairs"
              name="chairs"
              :error="$v.car.chairs.$error"
            />
            <template #error>
              <div v-if="$v.car.chairs.$dirty && !$v.car.chairs.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-7-4>
          <app-form-field label="Umbrellas" label-for="umbrellas" required>
            <app-input
              v-model.number="car.umbrellas"
              type="number"
              placeholder="123"
              id="umbrellas"
              name="umbrellas"
              :error="$v.car.umbrellas.$error"
            />
            <template #error>
              <div v-if="$v.car.umbrellas.$dirty && !$v.car.umbrellas.required">Required field</div>
            </template>
          </app-form-field>
        </template>
      </app-flex-grid>
    </app-page-container>
  </div>
</template>

<script>
import { getSafariGroupTypes, getSafariRentalCar, getSafariRentalCompany, patchSafariCar } from '@/http'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'Detail',
  data() {
    return {
      is_loaded: false,
      car: {},
      company_options: [],
      car_type_options: [],
      location_options: [],
    }
  },
  validations: {
    car: {
      rental_company: { required },
      year: { required },
      seats: { required },
      located: { required },
      car_type: { required },
      umbrellas: { required },
      binocular: { required },
      table: { required },
      chairs: { required },
      plate_number: { required },
    },
  },
  created() {
    Promise.all([
      getSafariRentalCar(this.$route.params.id),
      getSafariRentalCompany(),
      getSafariGroupTypes({ reservations: true })
    ])
      .then(res => {
        this.car = res[0].data
        this.company_options = res[1].data
        this.car_type_options = res[2].data
        this.is_loaded = true
      })
  },
  methods: {
    onSaveCarDetails() {
      this.$v.car.$touch()
      if (this.$v.car.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.$refs.save_details.preload = true
        patchSafariCar(this.$route.params.id, this.normalizeCarDetailsForm())
          .finally(() => { this.$refs.save_details.preload = false })
          .then(() => {
            this.$notify({
              type: 'success',
              title: 'Data saved',
            })
          })
      }
    },
    normalizeCarDetailsForm() {
      const {
        notes,
        active,
        rental_company,
        plate_number,
        located,
        seats,
        year,
        car_type,
        seats_movable,
        medical_kit,
        front_a_c,
        rear_a_c,
        wifi,
        books,
        fridge,
        hot_pots,
        coffee_bucket,
        registration_card,
        ssid,
        password,
        binocular,
        table,
        chairs,
        umbrellas
      } = this.car

      return {
        notes,
        active,
        rental_company: rental_company.id,
        plate_number,
        located,
        seats,
        year,
        car_type: car_type.code,
        seats_movable,
        medical_kit,
        front_a_c,
        rear_a_c,
        wifi,
        books,
        fridge,
        hot_pots,
        coffee_bucket,
        registration_card: registration_card ? registration_card.id : null,
        ssid,
        password,
        binocular,
        table,
        chairs,
        umbrellas
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.car__wrapper
  max-width: 880px

  +max-w($laptop_md)
    max-width: 100%

.car__name
  display: flex
  align-items: center
  gap: 10px
  margin-bottom: 25px
  color: #181818
  font-weight: 500

.car__name--no-indent
  margin-bottom: 0

.car__part
  margin-bottom: 20px

.car__part--more-indent
  margin-bottom: 40px

.car__row
  display: grid
  gap: 10px 20px
  grid-template-columns: repeat(4, 1fr)
  margin-bottom: 20px

  +max-w($laptop_md)
    grid-template-columns: repeat(2, 1fr)

.car__row--mix
  grid-template-columns: 205px 430px

.car__row--switchers
  grid-template-columns: repeat(5, 1fr)

.car__row--double
  grid-template-columns: 205px 205px
</style>
